/**
 * @hook useFullscreen
 * @description 跨瀏覽器全螢幕 API（含 Samsung Tizen webkit prefix）
 */

import { useState, useEffect, useCallback } from 'react';

interface FullscreenState {
  isFullscreen: boolean;
  isSupported: boolean;
  toggleFullscreen: () => Promise<void>;
  requestFullscreen: (el?: HTMLElement) => Promise<void>;
  exitFullscreen: () => Promise<void>;
}

function getFullscreenElement(): Element | null {
  return (
    document.fullscreenElement ??
    (document as unknown as { webkitFullscreenElement?: Element }).webkitFullscreenElement ??
    null
  );
}

function isFullscreenSupported(): boolean {
  return !!(
    document.documentElement.requestFullscreen ??
    (document.documentElement as unknown as { webkitRequestFullscreen?: () => void })
      .webkitRequestFullscreen
  );
}

export function useFullscreen(): FullscreenState {
  const [isFullscreen, setIsFullscreen] = useState(() => !!getFullscreenElement());
  const isSupported = isFullscreenSupported();

  useEffect(() => {
    const handler = () => setIsFullscreen(!!getFullscreenElement());
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
    };
  }, []);

  const requestFullscreen = useCallback(async (el?: HTMLElement) => {
    const target = el ?? document.documentElement;
    const wk = target as unknown as { webkitRequestFullscreen?: () => Promise<void> };
    if (target.requestFullscreen) {
      await target.requestFullscreen();
    } else if (wk.webkitRequestFullscreen) {
      await wk.webkitRequestFullscreen();
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    const wk = document as unknown as { webkitExitFullscreen?: () => Promise<void> };
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if (wk.webkitExitFullscreen) {
      await wk.webkitExitFullscreen();
    }
  }, []);

  const toggleFullscreen = useCallback(async () => {
    if (getFullscreenElement()) {
      await exitFullscreen();
    } else {
      await requestFullscreen();
    }
  }, [requestFullscreen, exitFullscreen]);

  return { isFullscreen, isSupported, toggleFullscreen, requestFullscreen, exitFullscreen };
}
